import { nullSafeToString } from '../lib/util';

function toParty (data, prefix) {
    return Object.freeze({
        name: nullSafeToString(data[`${prefix}Name`]),
        company: nullSafeToString(data[`${prefix}Company`]),
        phone: nullSafeToString(data[`${prefix}Phone`]),
        address: nullSafeToString(data[`${prefix}Address`]),
        address2: nullSafeToString(data[`${prefix}Address2`]),
        city: nullSafeToString(data[`${prefix}City`]),
        stateName: nullSafeToString(data[`${prefix}StateName`]),
        stateAbbreviation: nullSafeToString(data[`${prefix}StateAbbreviation`]),
        postalCode: nullSafeToString(data[`${prefix}Zip`]),
        countryName: nullSafeToString(data[`${prefix}CountryName`]),
        countryAbbreviation: nullSafeToString(data[`${prefix}CountryAbbreviation`]),
    });
}

/**
 * One shipment on a submitted {@link Order Order}, and the only place the order's full ship-to street
 * address is available - an {@link Order Order}'s own `shippingSummary` resolves the destination no
 * further than country, state and postal code.
 *
 * An order can have several of these. {@link Collaterate.Order.getShipments Collaterate.Order.getShipments}
 * returns all of them, which is what to render when an order ships to more than one place.
 *
 * `shipFrom` is specifically the **blind** ship-from address, not the supplier's origin. The server
 * writes the two together (`setAllShipmentsShippingAddressInfo`) and explicitly nulls every shipFrom
 * field when a shipment is not blind shipped, so the block is populated only when `isBlindShip` is
 * true and is empty otherwise. There is no endpoint that reveals the real origin to a storefront,
 * which is the point of blind shipping.
 *
 * Not to be confused with {@link CheckoutShipment CheckoutShipment}, which is the same wire shape read
 * mid-checkout to confirm what a save persisted. This one is the shipment after the fact, and adds the
 * `id`, carrier vendor and per-package tracking numbers that only exist once an order is real.
 *
 * @property {number} id - (Read only) This shipment's unique id.
 * @property {?number} number - (Read only) The shipment number, as shown to the shopper.
 * @property {OrderShipment~shipmentParty} shipTo - (Read only) Where this shipment is going, including street address.
 * @property {OrderShipment~shipmentParty} shipFrom - (Read only) The blind ship-from address: who the recipient should see as the sender. Every field is empty unless this.isBlindShip is true - see the note on the class.
 * @property {?number} shippingServiceId - (Read only) The selected shipping service id. Null when none is selected, or when a custom service was described instead.
 * @property {string} shippingServiceName - (Read only) The selected shipping service name.
 * @property {string} shippingServiceNotes - (Read only) Free text describing the service. Carries the custom service description when one was given.
 * @property {string} vendorName - (Read only) The carrier this shipment goes out with. Empty when a custom service was described instead.
 * @property {string} trackingNumber - (Read only) The shipment-level carrier tracking number, once available. Empty before it ships.
 * @property {?Date} shipBy - (Read only) The date this shipment is committed to ship by. Null if none is set.
 * @property {boolean} isBlindShip - (Read only) Whether this shipment is blind shipped, hiding the supplier from the recipient.
 * @property {boolean} isSaturdayDelivery - (Read only) Whether Saturday delivery was requested.
 * @property {boolean} isSignatureOnDelivery - (Read only) Whether signature on delivery was requested.
 * @property {string} referenceNumber1 - (Read only) The first shipping reference number.
 * @property {string} referenceNumber2 - (Read only) The second shipping reference number.
 * @property {OrderShipment~package[]} packages - (Read only) The packages in this shipment, each with its own tracking number. Empty until the shipment is packed.
 */

class OrderShipment {
    /*
        These fields appear in the server response, but are not mapped at present:
        - enabled
        - fedexIntegrated, upsIntegrated, uspsIntegrated (which carrier API backs the service; a
          storefront cannot act on it, and vendorName already says who is carrying the shipment)
    */

    constructor (data = {}) {
        this.id = data.id;
        this.number = data.number || null;

        this.shipTo = toParty(data, 'shipTo');
        this.shipFrom = toParty(data, 'shipFrom');

        this.shippingServiceId = data.shippingServiceId || null;
        this.shippingServiceName = nullSafeToString(data.shippingServiceName);
        this.shippingServiceNotes = nullSafeToString(data.shippingServiceNotes);
        this.vendorName = nullSafeToString(data.shippingVendor && data.shippingVendor.name);

        this.trackingNumber = nullSafeToString(data.trackingNumber);
        this.shipBy = data.shipBy ? Object.freeze(new Date(data.shipBy)) : null;

        this.isBlindShip = Boolean(data.blindShip);
        this.isSaturdayDelivery = Boolean(data.saturdayDelivery);
        this.isSignatureOnDelivery = Boolean(data.signatureOnDelivery);

        this.referenceNumber1 = nullSafeToString(data.referenceNumber1);
        this.referenceNumber2 = nullSafeToString(data.referenceNumber2);

        /*
         * A multi-package shipment gets a tracking number per package, and the shipment-level one is
         * only the first of them - so a tracking UI should read these rather than this.trackingNumber
         * whenever the array is non-empty.
         */
        this.packages = Object.freeze((data.packages || []).map(item => Object.freeze({
            id: item.id,
            length: item.length || 0,
            width: item.width || 0,
            height: item.height || 0,
            weight: item.weight || 0,
            trackingNumber: nullSafeToString(item.shippingTrackingNumber),
        })));

        Object.freeze(this);
    }
}

export default OrderShipment;

/**
 * One end of a shipment, as resolved by the server.
 *
 * @typedef {Object} OrderShipment~shipmentParty
 * @property {string} name - Contact name.
 * @property {string} company - Company name.
 * @property {string} phone - Contact phone.
 * @property {string} address - Street address.
 * @property {string} address2 - Second street address line.
 * @property {string} city - City.
 * @property {string} stateName - State name.
 * @property {string} stateAbbreviation - State abbreviation.
 * @property {string} postalCode - Postal code.
 * @property {string} countryName - Country name.
 * @property {string} countryAbbreviation - Country abbreviation.
 */

/**
 * A single package within a shipment. Dimensions are in the supplier's configured units.
 *
 * @typedef {Object} OrderShipment~package
 * @property {number} id - The package's unique id.
 * @property {number} length - Package length.
 * @property {number} width - Package width.
 * @property {number} height - Package height.
 * @property {number} weight - Package weight.
 * @property {string} trackingNumber - This package's own carrier tracking number. Empty before it ships.
 */
